import { DataAccess } from "../../../dataaccess/data.access";
import { Item } from "../../../pojo/Item";

export function isOrgOwnedEmailTemplate(template: SeEmailTemplateDto, orgId: number): boolean {
    return template.itemTypeId === Item.Ids.Organization && template.itemId != null && template.itemId === orgId;
}

export interface SeEmailTemplateDto {
    emailTemplateId?: number;
    templateName: string;
    subject?: string;
    bodyHtml?: string;
    itemId?: number | null;
    itemTypeId?: number | null;
    category?: string;
    enabled?: boolean;
    deleted?: boolean;
    createdContId?: number;
    createdDt?: string;
    modifiedContId?: number;
    modifiedDt?: string;
}

export interface SeEmailTemplateOrgAccessUpdateEntry {
    emailTemplateId: number;
    accountIds: number[];
}

/**
 * PUT body root for {@code orgs.json}: MVC Jackson {@code UNWRAP_ROOT_VALUE} expects property {@code "List"}
 * for {@code List<SeEmailTemplateOrgAccessUpdateEntry>}.
 */
export interface SeEmailTemplateOrgAccessUpdateEntryListRoot {
    List: SeEmailTemplateOrgAccessUpdateEntry[];
}

const BASE = "/student-engagement/email-templates";

export class Connect25EmailTemplateApi {
    static listInstitutionTemplates(): Promise<SeEmailTemplateDto[]> {
        return DataAccess.get(`${BASE}/admin/templates.json`);
    }

    static getInstitutionTemplate(emailTemplateId: number): Promise<SeEmailTemplateDto> {
        const params = DataAccess.params({ id: emailTemplateId });
        return DataAccess.get(`${BASE}/admin/template.json?${params}`);
    }

    static createInstitutionTemplate(body: SeEmailTemplateDto): Promise<SeEmailTemplateDto> {
        return DataAccess.post(`${BASE}/admin/template.json`, body);
    }

    static updateInstitutionTemplate(body: SeEmailTemplateDto): Promise<SeEmailTemplateDto> {
        return DataAccess.put(`${BASE}/admin/template.json`, body);
    }

    static deleteInstitutionTemplate(emailTemplateId: number): Promise<void> {
        const params = DataAccess.params({ id: emailTemplateId });
        return DataAccess.delete(`${BASE}/admin/template.json?${params}`);
    }

    static getOrgAllowlists(emailTemplateIds: number[]): Promise<SeEmailTemplateOrgAccessUpdateEntry[]> {
        const params = DataAccess.params({ ids: emailTemplateIds.join("+") });
        return DataAccess.get(`${BASE}/orgs.json?${params}`);
    }

    static putOrgAllowlists(
        entries: SeEmailTemplateOrgAccessUpdateEntry[],
    ): Promise<SeEmailTemplateOrgAccessUpdateEntry[]> {
        const body: SeEmailTemplateOrgAccessUpdateEntryListRoot = { List: entries };
        return DataAccess.put(`${BASE}/orgs.json`, body);
    }

    static listForOrg(orgId: number, includeBody = false, enabledOnly = true): Promise<SeEmailTemplateDto[]> {
        const params = DataAccess.params({ orgId, includeBody, enabledOnly });
        return DataAccess.get(`${BASE}/list.json?${params}`);
    }

    static listInstitutionIntersectionForOrgs(orgIds: number[], includeBody = false): Promise<SeEmailTemplateDto[]> {
        const params = DataAccess.params({ orgId: orgIds.join("+"), includeBody });
        return DataAccess.get(`${BASE}/list-intersection.json?${params}`);
    }

    static getForOrg(emailTemplateId: number, orgId: number, includeBody = true): Promise<SeEmailTemplateDto> {
        const params = DataAccess.params({ id: emailTemplateId, orgId, includeBody });
        return DataAccess.get(`${BASE}/template.json?${params}`);
    }

    static createOrgTemplate(orgId: number, body: SeEmailTemplateDto): Promise<SeEmailTemplateDto> {
        const params = DataAccess.params({ orgId });
        return DataAccess.post(`${BASE}/template.json?${params}`, body);
    }

    static updateOrgTemplate(orgId: number, body: SeEmailTemplateDto): Promise<SeEmailTemplateDto> {
        const params = DataAccess.params({ orgId });
        return DataAccess.put(`${BASE}/template.json?${params}`, body);
    }

    static deleteOrgTemplate(emailTemplateId: number, orgId: number): Promise<void> {
        const params = DataAccess.params({ id: emailTemplateId, orgId });
        return DataAccess.delete(`${BASE}/template.json?${params}`);
    }
}
